import React, { useState, useEffect } from 'react';
import {
  Form,
  DatePicker,
  TimePicker,
  Button,
  message,
  Table,
  Spin,
  Divider,
  Select,
  Checkbox,
  Typography,
  Tag
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { supabase } from '../../api/supabase';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';

const { Text, Title } = Typography;
const { Option } = Select;

// Monochrome styles
const styles = {
  page: {
    padding: spacing.xxl,
    maxWidth: '100%',
    minHeight: '100vh',
    background: colors.bgPrimary,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xl,
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: `${spacing.lg} ${spacing.xl}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerIcon: {
    width: '44px',
    height: '44px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    marginBottom: spacing.xl,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: `${spacing.lg} ${spacing.xl}`,
    borderBottom: `1px solid ${colors.borderSubtle}`,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },
  cardBody: {
    padding: spacing.xl,
  },
  yachtCard: {
    border: `1px solid ${colors.borderLight}`,
    borderLeft: `4px solid ${colors.bgDark}`,
    borderRadius: radius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    background: colors.bgSecondary,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    minWidth: '280px',
  },
  infoBox: {
    border: `1px solid ${colors.borderLight}`,
    borderLeft: `4px solid ${colors.bgDark}`,
    background: colors.bgTertiary,
    borderRadius: radius.lg,
    padding: spacing.xl,
  },
};

function BerthDocumentGenerator() {
  const [form] = Form.useForm();
  const [yachts, setYachts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedYachts, setSelectedYachts] = useState([]);
  const [yachtTimes, setYachtTimes] = useState({});
  const [yachtDates, setYachtDates] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchYachtsAndTemplates();
  }, []);

  const fetchYachtsAndTemplates = async () => {
    setLoading(true);
    try {
      const { data: yachtsData, error: yachtsError } = await supabase.from('yachts').select('id, name, brand').order('name');
      if (yachtsError) throw yachtsError;
      setYachts(yachtsData);

      const { data: filesData, error: filesError } = await supabase.storage.from('templates').list('', { limit: 100, sortBy: { column: 'name', order: 'asc' } });
      if (filesError) throw filesError;

      const templatesWithYachts = filesData.filter(file => file.name.endsWith('.pdf') && file.name.includes('place-a-quai')).map(file => {
        const yachtName = file.name.replace('-place-a-quai.pdf', '').replace(/-/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        const yacht = yachtsData.find(y => y.name.toLowerCase() === yachtName.toLowerCase());
        return { id: file.id, name: file.name, path: file.name, yachtId: yacht?.id, yachtName: yacht?.name || yachtName, size: file.metadata?.size };
      });

      setTemplates(templatesWithYachts);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Erreur lors du chargement: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async (templatePath) => {
    try {
      const { data, error } = await supabase.storage.from('templates').download(templatePath);
      if (error) throw error;
      return await data.arrayBuffer();
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  };

  const replacePDFText = async (pdfBytes, variables) => {
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes);

      try {
        const form = pdfDoc.getForm();
        const fields = form.getFields();

        // Map PDF field names to our variable keys
        // Supports both template placeholders AND French field names from the actual PDFs
        const fieldNameMapping = {
          // arrival_date mappings
          'Date darrivée': 'arrival_date',
          "Date d'arrivée": 'arrival_date',
          'arrival_date': 'arrival_date',
          '{{arrival_date}}': 'arrival_date',

          // departure_date mappings
          'Date de départ': 'departure_date',
          'departure_date': 'departure_date',
          '{{departure_date}}': 'departure_date',

          // arrival_hour mappings
          'Heure darrivée': 'arrival_hour',
          "Heure d'arrivée": 'arrival_hour',
          'arrival_hour': 'arrival_hour',
          '{{arrival_hour}}': 'arrival_hour',

          // departure_hour mappings
          'Heure de départ': 'departure_hour',
          'departure_hour': 'departure_hour',
          '{{departure_hour}}': 'departure_hour',
        };

        // Clear all text fields first to avoid doubling with pre-filled values
        fields.forEach((field) => {
          if (typeof field.setText === 'function') {
            try { field.setText(''); } catch (e) {}
          }
        });

        fields.forEach((field) => {
          const fieldName = field.getName();
          const isTextField = typeof field.setText === 'function';
          const isDropdown = typeof field.select === 'function' && !isTextField;

          const variableKey = fieldNameMapping[fieldName];

          if (variableKey && variables[variableKey]) {
            try {
              if (isTextField) {
                field.setText(variables[variableKey]);
              } else if (isDropdown) {
                field.select(variables[variableKey]);
              }
            } catch (e) {
              console.error(`Error setting field "${fieldName}":`, e);
            }
          }
        });

        try { form.flatten(); } catch (e) {}
      } catch (e) {
        console.warn('No form fields in PDF:', e.message);
      }

      return await pdfDoc.save();
    } catch (error) {
      console.error('Error modifying PDF:', error);
      throw error;
    }
  };

  const handleGenerate = async (values) => {
    if (selectedYachts.length === 0) { message.error('Veuillez sélectionner au moins un bateau'); return; }
    setGenerating(true);

    try {
      const zip = new JSZip();
      const folder = zip.folder('Places_a_Quai');
      const selectedTemplates = templates.filter(t => selectedYachts.includes(t.yachtId));

      if (selectedTemplates.length === 0) { message.error('Aucun template trouvé pour les bateaux sélectionnés'); setGenerating(false); return; }

      for (const template of selectedTemplates) {
        try {
          const yachtId = template.yachtId;
          const times = yachtTimes[yachtId] || {};
          const dates = yachtDates[yachtId] || {};
          const arrivalDate = dates.arrival_date || values.arrival_date;
          const departureDate = dates.departure_date || values.departure_date;

          const variables = {
            arrival_date: arrivalDate ? dayjs(arrivalDate).format('DD/MM/YYYY') : '',
            departure_date: departureDate ? dayjs(departureDate).format('DD/MM/YYYY') : '',
            arrival_hour: times.arrival_hour ? times.arrival_hour.format('HH:mm') : '',
            departure_hour: times.departure_hour ? times.departure_hour.format('HH:mm') : '',
          };

          const pdfBytes = await downloadTemplate(template.path);
          const modifiedPdfBytes = await replacePDFText(pdfBytes, variables);
          const fileName = `${template.yachtName}_${dayjs(arrivalDate).format('DDMMYYYY')}_${dayjs(departureDate).format('DDMMYYYY')}.pdf`;
          folder.file(fileName, modifiedPdfBytes);
        } catch (error) {
          message.warning(`Erreur avec ${template.name}, il sera ignoré`);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `Places_a_Quai_${dayjs().format('DDMMYYYY_HHmmss')}.zip`);
      message.success(`${selectedTemplates.length} document(s) générés avec succès!`);
    } catch (error) {
      message.error('Erreur lors de la génération: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleYachtSelect = (value) => {
    setSelectedYachts(value);
    const newTimes = { ...yachtTimes };
    value.forEach(yachtId => {
      if (!newTimes[yachtId]) {
        newTimes[yachtId] = { arrival_hour: dayjs().set('hour', 9).set('minute', 0), departure_hour: dayjs().set('hour', 17).set('minute', 0) };
      }
    });
    setYachtTimes(newTimes);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allYachtIds = templates.filter(t => t.yachtId).map(t => t.yachtId);
      setSelectedYachts([...new Set(allYachtIds)]);
      const newTimes = { ...yachtTimes };
      allYachtIds.forEach(yachtId => {
        if (!newTimes[yachtId]) newTimes[yachtId] = { arrival_hour: dayjs().set('hour', 9).set('minute', 0), departure_hour: dayjs().set('hour', 17).set('minute', 0) };
      });
      setYachtTimes(newTimes);
    } else {
      setSelectedYachts([]);
    }
  };

  const handleTimeChange = (yachtId, field, value) => setYachtTimes(prev => ({ ...prev, [yachtId]: { ...prev[yachtId], [field]: value } }));
  const handleDateChange = (yachtId, field, value) => setYachtDates(prev => ({ ...prev, [yachtId]: { ...prev[yachtId], [field]: value } }));

  const columns = [
    { title: 'Yacht', dataIndex: 'yachtName', key: 'yachtName', render: (name) => <span><FileTextOutlined style={{ marginRight: '8px' }} /><strong>{name}</strong></span> },
    { title: 'Template File', dataIndex: 'name', key: 'name' },
    { title: 'Status', key: 'status', render: (_, record) => <span style={{ color: record.yachtId ? colors.accentSuccess : colors.accentOrange }}>{record.yachtId ? '✓ Linked' : '⚠ Not linked'}</span> },
  ];

  return (
    <div style={styles.page}>
      <style>
        {`
          .ant-form-item-label > label { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textSecondary} !important; text-transform: uppercase; letter-spacing: 0.5px; }
          .ant-picker-input > input { color: ${colors.textPrimary} !important; font-weight: 500 !important; }
          .ant-picker-dropdown .ant-picker-cell-in-view { color: ${colors.textPrimary} !important; font-weight: 600 !important; }
          .ant-picker-time-panel-column > li { color: ${colors.textPrimary} !important; font-weight: 500 !important; }
          .ant-table-thead > tr > th { background: ${colors.bgTertiary} !important; font-weight: 600 !important; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
          .ant-divider-inner-text { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textMuted} !important; text-transform: uppercase; letter-spacing: 1px; }
        `}
      </style>

      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
          <div style={styles.headerIcon}>⚓</div>
          <div>
            <Title level={4} style={{ margin: 0, color: colors.textPrimary }}>Place à quai</Title>
            <Text style={{ fontSize: typography.fontSize.xs, color: colors.textTertiary }}>
              {templates.length} template{templates.length > 1 ? 's' : ''} available
            </Text>
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchYachtsAndTemplates} loading={loading} style={{ borderRadius: radius.lg, border: `1px solid ${colors.borderMedium}` }}>
          Refresh
        </Button>
      </div>

      {/* Templates Table */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <FileTextOutlined style={{ fontSize: '18px', color: colors.textPrimary }} />
          <span style={{ fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>Available Templates</span>
          <Tag style={{ marginLeft: spacing.sm, background: colors.bgDark, color: colors.textInverse, border: 'none' }}>{templates.length}</Tag>
        </div>
        <div style={styles.cardBody}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: spacing.xxxl }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: spacing.lg, color: colors.textTertiary }}>Loading templates...</Text>
            </div>
          ) : (
            <Table columns={columns} dataSource={templates} rowKey="id" pagination={false} size="middle" />
          )}
        </div>
      </div>

      {/* Configuration Form */}
      {!loading && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <CalendarOutlined style={{ fontSize: '18px', color: colors.textPrimary }} />
            <span style={{ fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>Configuration</span>
          </div>
          <div style={styles.cardBody}>
            <Form form={form} layout="vertical" onFinish={handleGenerate} initialValues={{ arrival_date: dayjs().add(1, 'day'), departure_date: dayjs().add(1, 'day') }}>
              <div style={{ marginBottom: spacing.xl }}>

                <Select mode="multiple" placeholder="Select one or more yachts" style={{ width: '100%' }} size="large" value={selectedYachts} onChange={handleYachtSelect} showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                  {templates.filter(t => t.yachtId).map(t => <Option key={t.yachtId} value={t.yachtId}>{t.yachtName}</Option>)}
                </Select>
              </div>

              <Divider orientation="left"><Text type="secondary" style={{ fontSize: typography.fontSize.sm }}>Common dates for all yachts</Text></Divider>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.xl }}>
                <Form.Item name="departure_date" label="Departure Date" rules={[{ required: true, message: 'Required' }]}>
                  <DatePicker format="DD/MM/YYYY" size="large" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="arrival_date" label="Arrival Date" rules={[{ required: true, message: 'Required' }]}>
                  <DatePicker format="DD/MM/YYYY" size="large" style={{ width: '100%' }} />
                </Form.Item>
              </div>

              {selectedYachts.length > 0 && (
                <>
                  <Divider orientation="left"><Text type="secondary" style={{ fontSize: typography.fontSize.sm }}>Specific times and dates ({selectedYachts.length} yacht{selectedYachts.length > 1 ? 's' : ''})</Text></Divider>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                    {selectedYachts.map((yachtId) => {
                      const template = templates.find(t => t.yachtId === yachtId);
                      if (!template) return null;

                      return (
                        <div key={yachtId} style={styles.yachtCard}>
                          <div style={{ marginBottom: spacing.lg }}>
                            <Text strong style={{ fontSize: typography.fontSize.md }}>🚢 {template.yachtName}</Text>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.lg }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: spacing.xs, fontWeight: typography.fontWeight.medium, color: colors.textSecondary, fontSize: typography.fontSize.xs, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <ClockCircleOutlined /> Arrival Time *
                              </label>
                              <TimePicker format="HH:mm" size="large" style={{ width: '100%' }} value={yachtTimes[yachtId]?.arrival_hour || dayjs().set('hour', 9).set('minute', 0)} onChange={(value) => handleTimeChange(yachtId, 'arrival_hour', value)} placeholder="09:00" showNow={false} />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: spacing.xs, fontWeight: typography.fontWeight.medium, color: colors.textSecondary, fontSize: typography.fontSize.xs, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <ClockCircleOutlined /> Departure Time *
                              </label>
                              <TimePicker format="HH:mm" size="large" style={{ width: '100%' }} value={yachtTimes[yachtId]?.departure_hour || dayjs().set('hour', 17).set('minute', 0)} onChange={(value) => handleTimeChange(yachtId, 'departure_hour', value)} placeholder="17:00" showNow={false} />
                            </div>
                          </div>

                          <div style={{ background: colors.bgTertiary, padding: spacing.sm, borderRadius: radius.md, marginBottom: spacing.md }}>
                            <Text type="secondary" style={{ fontSize: typography.fontSize.xs }}>Optional: Different dates (if empty, uses common dates)</Text>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: spacing.xs, fontWeight: typography.fontWeight.medium, color: colors.textTertiary, fontSize: typography.fontSize.xs }}>Departure Date (opt.)</label>
                              <DatePicker format="DD/MM/YYYY" size="large" style={{ width: '100%' }} value={yachtDates[yachtId]?.departure_date || null} onChange={(value) => handleDateChange(yachtId, 'departure_date', value)} placeholder="Uses common date" allowClear />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: spacing.xs, fontWeight: typography.fontWeight.medium, color: colors.textTertiary, fontSize: typography.fontSize.xs }}>Arrival Date (opt.)</label>
                              <DatePicker format="DD/MM/YYYY" size="large" style={{ width: '100%' }} value={yachtDates[yachtId]?.arrival_date || null} onChange={(value) => handleDateChange(yachtId, 'arrival_date', value)} placeholder="Uses common date" allowClear />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Button type="primary" htmlType="submit" icon={<DownloadOutlined />} size="large" loading={generating} disabled={selectedYachts.length === 0} style={styles.primaryBtn}>
                  Generate and Download ({selectedYachts.length} yacht{selectedYachts.length > 1 ? 's' : ''})
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div style={styles.infoBox}>
        <div style={{ marginBottom: spacing.md }}>
          <Text strong style={{ fontSize: typography.fontSize.md }}>PDF Variables</Text>
        </div>
        <ul style={{ marginBottom: spacing.lg, paddingLeft: spacing.xl, color: colors.textPrimary }}>
          <li style={{ marginBottom: spacing.xs }}><code style={{ background: colors.bgSecondary, padding: '2px 8px', borderRadius: radius.sm, fontSize: typography.fontSize.xs }}>{'{{arrival_date}}'}</code> - Arrival date (DD/MM/YYYY)</li>
          <li style={{ marginBottom: spacing.xs }}><code style={{ background: colors.bgSecondary, padding: '2px 8px', borderRadius: radius.sm, fontSize: typography.fontSize.xs }}>{'{{departure_date}}'}</code> - Departure date (DD/MM/YYYY)</li>
          <li style={{ marginBottom: spacing.xs }}><code style={{ background: colors.bgSecondary, padding: '2px 8px', borderRadius: radius.sm, fontSize: typography.fontSize.xs }}>{'{{arrival_hour}}'}</code> - Arrival time (HH:mm)</li>
          <li><code style={{ background: colors.bgSecondary, padding: '2px 8px', borderRadius: radius.sm, fontSize: typography.fontSize.xs }}>{'{{departure_hour}}'}</code> - Departure time (HH:mm)</li>
        </ul>
        <div style={{ background: colors.bgSecondary, padding: spacing.md, borderRadius: radius.md, border: `1px solid ${colors.borderSubtle}` }}>
          <Text style={{ fontSize: typography.fontSize.xs, color: colors.textTertiary }}>
            <strong>Note:</strong> Templates must be uploaded to Supabase Storage (bucket: <code>templates</code>) with filename format: <code>{'{yacht-name}-place-a-quai.pdf'}</code>
          </Text>
        </div>
      </div>
    </div>
  );
}

export default BerthDocumentGenerator;
